import {
  CheckIcon,
  ChevronDownIcon,
  FileUpIcon,
  GitCompareIcon,
  ListIcon,
  LogInIcon,
  LogOutIcon,
  UserIcon,
} from "lucide-react";
import { Link, useLocation } from "react-router";
import { switchProfile } from "../lib/api";
import { useSession } from "../lib/use-session";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Przycisk logowania albo nazwa zalogowanego konta.
 *
 * Logowanie jest zwykłym linkiem, a nie wywołaniem `fetch`: przepływ OIDC to
 * seria przekierowań przeglądarki, których nie da się przeprowadzić z XHR-a.
 */
export function AuthCorner() {
  const { loading, config, session, signOut, refresh } = useSession();
  const profiles = session?.profiles ?? [];
  const activeProfileId = session?.activeProfileId;

  /**
   * Po przełączeniu odświeżamy sesję **i przeładowujemy stronę**.
   *
   * Twarde przeładowanie, nie tylko `refresh()`: postęp, biblioteka i „Kontynuuj
   * oglądanie" są już wczytane w ekranach, które o zmianie profilu nie wiedzą.
   * Bez tego widz zostałby z półką poprzedniego profilu i musiałby sam zgadnąć,
   * że trzeba odświeżyć — a półka z cudzymi odcinkami to dokładnie ten defekt,
   * przed którym profile mają chronić.
   */
  async function switchTo(profileId: string): Promise<void> {
    try {
      await switchProfile(profileId);
      refresh();
      window.location.reload();
    } catch {
      // Cicho: jedyny realny powód to wyścig z usunięciem profilu w innej karcie,
      // a wtedy przeładowanie i tak pokaże stan zgodny z serwerem.
      window.location.reload();
    }
  }
  const location = useLocation();

  if (loading) {
    return <span className="px-2 text-sm text-muted-foreground">…</span>;
  }

  if (session?.authenticated === true && session.user !== null) {
    const name = session.user.displayName ?? session.user.username;
    return (
      /*
        Menu, nie rząd przycisków w nagłówku.
        Rzeczy związane z kontem — import listy, wysyłanie pliku, wylogowanie —
        rozchodziły się po nawigacji albo nie miały wejścia w interfejsie wcale
        (`/import` był dostępny tylko z paska adresu). Nagłówek pełnoekranowej
        aplikacji ma być pusty; miejsce na takie rzeczy jest pod kontem.
      */
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 px-2">
            {/* Inicjały, nie zdjęcie: `/auth/me` nie zwraca dziś `avatarUrl`, a pusty
                `AvatarImage` dawałby tylko żądanie kończące się błędem. */}
            <Avatar className="size-7">
              <AvatarFallback>{name.slice(0, 2).toLocaleUpperCase("pl")}</AvatarFallback>
            </Avatar>
            <span className="hidden max-w-[10rem] truncate sm:inline">{name}</span>
            <ChevronDownIcon className="size-3.5 opacity-60" />
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent>
          <DropdownMenuLabel>
            {name}
            {session.user.isAdmin && <span className="ml-1 text-accent">· administrator</span>}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {/* Przełącznik profili NAD pozycjami konta: to zmiana tego, czyj postęp
              zapisujemy, więc jest ważniejsza od skrótów do importu i wysyłania.
              Pokazujemy go tylko przy więcej niż jednym profilu — lista z jedną
              pozycją i ptaszkiem obok wygląda jak przełącznik, który nie działa. */}
          {profiles.length > 1 && (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                Profil
              </DropdownMenuLabel>
              {profiles.map((p) => (
                <DropdownMenuItem
                  key={p.id}
                  onSelect={() => void switchTo(p.id)}
                  disabled={p.id === activeProfileId}
                >
                  {p.id === activeProfileId ? <CheckIcon /> : <UserIcon />}
                  {p.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
            </>
          )}

          {/* `asChild`, żeby pozycją menu był `Link` routera: zwykły `onSelect`
              z nawigacją odbiera obsługę środkowego przycisku myszy i „otwórz
              w nowej karcie". */}
          <DropdownMenuItem asChild>
            <Link to="/import">
              <ListIcon />
              Lista z MyAnimeList
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/wyslij">
              <FileUpIcon />
              Wyślij plik
            </Link>
          </DropdownMenuItem>

          {/* Przegląd mapowań zmienia katalog wszystkich, więc widzą go tylko
              administratorzy — pokazywanie go resztzie znaczyłoby wejście, które
              kończy się odmową. */}
          {session.user.isAdmin && (
            <DropdownMenuItem asChild>
              <Link to="/mapowania">
                <GitCompareIcon />
                Przegląd mapowań
              </Link>
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => void signOut()}>
            <LogOutIcon />
            Wyloguj
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  if (config?.enabled !== true) {
    // Bez skonfigurowanego dostawcy przycisk prowadziłby wyłącznie do błędu,
    // więc go nie pokazujemy. Aplikacja działa dalej — bez biblioteki i postępu.
    return null;
  }

  // Ścieżka powrotu bez części zapytania: mogłaby zawierać `oidcError`
  // z nieudanej próby i po zalogowaniu wciąż pokazywałaby ostrzeżenie.
  const returnTo = encodeURIComponent(location.pathname);

  return (
    <Button variant="hero" size="sm" asChild>
      <a href={`${config.loginUrl}?returnTo=${returnTo}`}>
        <LogInIcon />
        Zaloguj przez {config.providerName}
      </a>
    </Button>
  );
}

/** Kody z callbacku dostawcy w formie zrozumiałej dla użytkownika. */
export function describeOidcError(code: string): string {
  switch (code) {
    case "DISABLED":
      return "logowanie jest wyłączone na tej instancji";
    case "STATE_MISMATCH":
      return "próba wygasła albo została powtórzona — zacznij logowanie od nowa";
    case "MISSING_CODE":
    case "BAD_CALLBACK":
      return "dostawca nie przekazał kodu autoryzacji";
    case "EXCHANGE_FAILED":
      return "wymiana kodu na token nie powiodła się — sprawdź konfigurację klienta";
    case "VERIFY_FAILED":
      return "token tożsamości nie przeszedł weryfikacji";
    case "DISCOVERY_FAILED":
      return "nie udało się połączyć z dostawcą tożsamości";
    case "access_denied":
      return "odmówiono dostępu";
    default:
      return code;
  }
}
